/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Upload, Loader2, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { saveTier2, uploadRegistryEvidence, uploadCV, fileToDataURL, REGISTRY_TYPES_BY_CATEGORY } from '@/lib/onboarding';
import { ErrorAlert } from './parts';

// -----------------------------------------------------------------------------
// TIER 2: Professional verification
// -----------------------------------------------------------------------------
export const Tier2Step = ({ user, status, onDone }) => {
  const catKey = (user?.category || 'medici').toLowerCase();
  const registryOptions = REGISTRY_TYPES_BY_CATEGORY[catKey] || REGISTRY_TYPES_BY_CATEGORY.medici;

  const [form, setForm] = useState({
    registry_type: user?.registry_type || registryOptions[0].value,
    registry_number: user?.registry_number || user?.albo_number || '',
    specialty: user?.specialty || '',
    primary_work_location: user?.primary_work_location || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [cvBusy, setCvBusy] = useState(false);
  const [evidenceUploaded, setEvidenceUploaded] = useState(!!user?.registry_evidence_uploaded);

  const isPending = status?.registry_verification_status === 'pending';
  const isApproved = status?.registry_verification_status === 'approved';
  const isRejected = status?.registry_verification_status === 'rejected';

  const canSubmitProfile =
    !!form.registry_type &&
    /^\d{4,12}$/.test((form.registry_number || '').trim()) &&
    form.specialty.trim().length > 0 &&
    form.primary_work_location.trim().length > 0;

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await saveTier2(form);
      toast.success('Professional data saved');
    } catch (e) { setError(e); }
    finally { setSaving(false); }
  };

  const handleEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEvidenceBusy(true); setError(null);
    try {
      const dataUrl = await fileToDataURL(file);
      await uploadRegistryEvidence(dataUrl);
      setEvidenceUploaded(true);
      toast.success('Registry evidence uploaded — awaiting review');
      onDone && onDone();
    } catch (err) { setError(err); }
    finally { setEvidenceBusy(false); }
  };

  const handleCV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvBusy(true); setError(null);
    try {
      const dataUrl = await fileToDataURL(file);
      await uploadCV(dataUrl);
      toast.success('CV uploaded');
    } catch (err) { setError(err); }
    finally { setCvBusy(false); }
  };

  return (
    <div data-testid="tier2-step">
      <div className="mb-4">
        <h2 className="font-display font-bold text-xl mb-1">Tier 2 — Professional verification</h2>
        <p className="text-sm text-muted-foreground">
          Provide your official registration details. Access to core clinical features is unlocked
          only after our team manually reviews and approves your evidence (usually 24–48 hours).
        </p>
      </div>
      <ErrorAlert error={error} />

      {isPending && (
        <Alert className="mb-4" data-testid="tier2-pending-alert">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Your verification is <strong>under review</strong>. You will receive an email once complete.
          </AlertDescription>
        </Alert>
      )}
      {isRejected && (
        <Alert variant="destructive" className="mb-4" data-testid="tier2-rejected-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Verification was rejected. Please re-upload updated evidence.
            {user?.registry_verification_reject_reason && (
              <span> Reason: <em>{user.registry_verification_reject_reason}</em></span>
            )}
          </AlertDescription>
        </Alert>
      )}
      {isApproved && (
        <Alert className="mb-4 border-emerald-500/40" data-testid="tier2-approved-alert">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertDescription>
            Your professional verification has been <strong>approved</strong>. You can now proceed to Tier 3.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="reg_type">Registry type</Label>
          <select
            id="reg_type" data-testid="tier2-registry-type"
            value={form.registry_type}
            onChange={(e) => setForm({ ...form, registry_type: e.target.value })}
            disabled={isPending || isApproved}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            {registryOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="reg_num">Registry number</Label>
          <Input id="reg_num" data-testid="tier2-registry-number"
            value={form.registry_number}
            onChange={(e) => setForm({ ...form, registry_number: e.target.value.replace(/\D/g, '') })}
            disabled={isPending || isApproved} />
        </div>
        <div>
          <Label htmlFor="spec">Specialty / role</Label>
          <Input id="spec" data-testid="tier2-specialty"
            placeholder="Cardiology, Home Care, Elderly Care…"
            value={form.specialty}
            onChange={(e) => setForm({ ...form, specialty: e.target.value })}
            disabled={isPending || isApproved} />
        </div>
        <div>
          <Label htmlFor="loc">Primary work location</Label>
          <Input id="loc" data-testid="tier2-location"
            placeholder="Milano, MI"
            value={form.primary_work_location}
            onChange={(e) => setForm({ ...form, primary_work_location: e.target.value })}
            disabled={isPending || isApproved} />
        </div>
      </div>
      {!isPending && !isApproved && (
        <div className="mt-4 flex justify-end">
          <Button onClick={handleSave} disabled={!canSubmitProfile || saving} data-testid="tier2-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save professional data
          </Button>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="font-display font-semibold text-base mb-2 flex items-center gap-2">
          <Upload className="h-4 w-4 text-primary" /> Registry evidence (screenshot / scan)
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Upload a clear PNG/JPG image of your Albo (medici), OPI card (infermieri) or OSS certificate. Max 5 MB.
        </p>
        <label className="inline-flex">
          <input type="file" accept="image/png,image/jpeg,image/webp"
            onChange={handleEvidence} disabled={evidenceBusy || isPending || isApproved}
            className="hidden-file" data-testid="tier2-evidence-input" />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 cursor-pointer text-sm font-medium">
            {evidenceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {evidenceUploaded ? 'Re-upload evidence' : 'Upload evidence'}
          </span>
        </label>
        {evidenceUploaded && (
          <Badge variant="outline" className="ml-2" data-testid="tier2-evidence-uploaded-badge">Uploaded</Badge>
        )}

        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-1">Curriculum Vitae (optional, PDF, max 8 MB)</h4>
          <label className="inline-flex">
            <input type="file" accept="application/pdf"
              onChange={handleCV} disabled={cvBusy}
              className="hidden-file" data-testid="tier2-cv-input" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 cursor-pointer text-sm font-medium">
              {cvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload CV
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};
